import React, { useCallback, useEffect, useRef } from 'react'

/**
 * Tap fires one thing, holding fires another.
 *
 * Written here rather than pulled from a package: it is forty lines of pointer
 * bookkeeping and the repo had no long-press helper to reuse.
 *
 * Pointer events rather than mouse+touch pairs, so one code path covers mouse,
 * pen and finger. Three details are what make it feel right rather than
 * merely work:
 *
 * - **The tap fires on pointerup, not on click.** A long press is followed by
 *   a click on most platforms, so a `onClick` tap handler would run *as well
 *   as* the long-press action. Firing on pointerup lets us gate on whether the
 *   hold already fired.
 * - **Keyboard activation still works.** A button activated with Enter or
 *   Space emits a click with `detail === 0` and no pointer events at all, so
 *   that case is routed to the tap handler explicitly. Long-press has no
 *   keyboard equivalent, which is why it must never be the only way to reach
 *   an action.
 * - **Movement cancels.** The pill row this was written for scrolls
 *   horizontally; a drag that starts on a pill is a scroll, not a press.
 */

export const LONG_PRESS_DELAY_MS = 500
const LONG_PRESS_MOVE_TOLERANCE_PX = 10

export interface UseLongPressOptions {
    /** Fired once the pointer has been held still for `delayMs`. */
    onLongPress: () => void
    /** Fired on a press that ended before the hold matured. */
    onTap?: () => void
    delayMs?: number
    /** A press that wanders further than this is a drag, and cancels. */
    moveTolerancePx?: number
}

export interface LongPressHandlers {
    onPointerDown: (event: React.PointerEvent) => void
    onPointerMove: (event: React.PointerEvent) => void
    onPointerUp: () => void
    onPointerCancel: () => void
    onContextMenu: (event: React.MouseEvent) => void
    onClick: (event: React.MouseEvent) => void
}

export function useLongPress({
    onLongPress,
    onTap,
    delayMs = LONG_PRESS_DELAY_MS,
    moveTolerancePx = LONG_PRESS_MOVE_TOLERANCE_PX,
}: UseLongPressOptions): LongPressHandlers {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const originRef = useRef<{ x: number; y: number } | null>(null)
    const firedRef = useRef(false)

    // The handlers below are stable across renders, so they read the current
    // callbacks through refs rather than closing over stale ones.
    const onLongPressRef = useRef(onLongPress)
    const onTapRef = useRef(onTap)
    useEffect(() => {
        onLongPressRef.current = onLongPress
        onTapRef.current = onTap
    })

    const clearTimer = useCallback(() => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }
    }, [])

    // An unmount mid-press (the row re-sorting under the finger, say) must not
    // leave a timer that fires into a dead component.
    useEffect(() => clearTimer, [clearTimer])

    const onPointerDown = useCallback((event: React.PointerEvent) => {
        // Secondary and middle buttons are not presses.
        if (event.button !== 0) return
        firedRef.current = false
        originRef.current = { x: event.clientX, y: event.clientY }
        clearTimer()
        // Capture so a press that drifts off the element still delivers its
        // move/up events here, instead of stranding the timer. Best-effort:
        // it throws on a pointer id the browser doesn't consider active,
        // which is every synthetic PointerEvent a test dispatches.
        try {
            event.currentTarget.setPointerCapture?.(event.pointerId)
        } catch {
            // The move/cancel handlers below still bound the press without it.
        }
        timerRef.current = setTimeout(() => {
            timerRef.current = null
            firedRef.current = true
            onLongPressRef.current()
        }, delayMs)
    }, [clearTimer, delayMs])

    const onPointerMove = useCallback((event: React.PointerEvent) => {
        const origin = originRef.current
        if (!origin || timerRef.current === null) return
        if (
            Math.abs(event.clientX - origin.x) > moveTolerancePx ||
            Math.abs(event.clientY - origin.y) > moveTolerancePx
        ) {
            clearTimer()
            originRef.current = null
        }
    }, [clearTimer, moveTolerancePx])

    const onPointerUp = useCallback(() => {
        const wasPressing = originRef.current !== null
        const holdStillPending = timerRef.current !== null
        clearTimer()
        originRef.current = null
        if (wasPressing && holdStillPending && !firedRef.current) {
            onTapRef.current?.()
        }
    }, [clearTimer])

    const onPointerCancel = useCallback(() => {
        clearTimer()
        originRef.current = null
    }, [clearTimer])

    // Holding on a touch device otherwise raises the platform's own
    // text-selection / context menu on top of whatever the hold opened.
    const onContextMenu = useCallback((event: React.MouseEvent) => {
        event.preventDefault()
    }, [])

    const onClick = useCallback((event: React.MouseEvent) => {
        // `detail === 0` means the click came from the keyboard, not a pointer
        // — the one case pointerup never covers.
        if (event.detail === 0) {
            onTapRef.current?.()
        }
    }, [])

    return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onContextMenu, onClick }
}

